import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Icon,
  Loading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SupportedIcon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy } from 'corso-types';
import { useMemo, useRef, useState } from 'react';
import { useDebounceValue, useEventListener } from 'usehooks-ts';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useStoreId } from '~/providers/StoreUserProvider';
import { getQuickSearchIcon } from './quickSearchIcon';
import { QuickSearchListItem } from './QuickSearchListItem';
import { useIsConciergeSearch, useSearchByKind } from './useSearchByKind';

function useSearchData(searchTerm: string) {
  const returnsQuery = useSearchByKind(searchTerm, 'Return');
  const warrantiesQuery = useSearchByKind(searchTerm, 'Warranty');
  const shippingQuery = useSearchByKind(searchTerm, 'Shipping');
  const registrationsQuery = useSearchByKind(searchTerm, 'Registration');
  const ordersQuery = useSearchByKind(searchTerm, 'Order');
  const productsQuery = useSearchByKind(searchTerm, 'Product');

  const isLoading = [
    returnsQuery,
    warrantiesQuery,
    shippingQuery,
    registrationsQuery,
    ordersQuery,
    productsQuery,
  ].some((query) => query.isLoading);

  const isRefetching = [
    returnsQuery,
    warrantiesQuery,
    shippingQuery,
    registrationsQuery,
    ordersQuery,
    productsQuery,
  ].some((query) => query.isRefetching);

  return useMemo(
    () => ({
      isLoading,
      isRefetching,
      // data effectively a select on the original request, since the response does not know kind requested
      returns:
        returnsQuery.data?.kind === 'Return' ? returnsQuery.data.data : [],
      warranties:
        warrantiesQuery.data?.kind === 'Warranty'
          ? warrantiesQuery.data.data
          : [],
      shipping:
        shippingQuery.data?.kind === 'Shipping' ? shippingQuery.data.data : [],
      registrations:
        registrationsQuery.data?.kind === 'Registration'
          ? registrationsQuery.data.data
          : [],
      orders: ordersQuery.data?.kind === 'Order' ? ordersQuery.data.data : [],
      products:
        productsQuery.data?.kind === 'Product' ? productsQuery.data.data : [],
    }),
    [
      isLoading,
      isRefetching,
      returnsQuery,
      warrantiesQuery,
      shippingQuery,
      registrationsQuery,
      ordersQuery,
      productsQuery,
    ],
  );
}

type QuickSearchItem = {
  key: string;
  to: string;
  externalId: string;
  customerName: string | null;
  createdOn: string;
  // products show where they come from instead of when they were created
  source: string | null;
  icon: SupportedIcon;
  storeName: string | null;
};

function useSearchGroups(searchTerm: string) {
  const storeId = useStoreId();
  const isConciergeSearch = useIsConciergeSearch();
  const {
    isLoading,
    isRefetching,
    returns,
    warranties,
    shipping,
    registrations,
    orders,
    products,
  } = useSearchData(searchTerm);

  // only a concierge sees results from more than one store, so only they need the name
  const storeNameOf = (storeName: string) =>
    isConciergeSearch ? storeName : null;

  const groups = [
    {
      heading: 'Returns',
      items: returns.map(
        (i) =>
          ({
            key: `returns-${i.id}`,
            to: `/${i.storeId}/claims/return/${i.id}`,
            externalId: `#${i.externalId}`,
            customerName: i.customerName,
            createdOn: i.createdOn,
            source: null,
            icon: 'exchange',
            storeName: storeNameOf(i.storeName),
          }) satisfies QuickSearchItem,
      ),
    },
    {
      heading: 'Warranties',
      items: warranties.map(
        (i) =>
          ({
            key: `warranties-${i.id}`,
            to: `/${i.storeId}/claims/warranty/${i.id}`,
            externalId: `#${i.externalId}`,
            customerName: i.customerName,
            createdOn: i.createdOn,
            source: null,
            icon: 'verified',
            storeName: storeNameOf(i.storeName),
          }) satisfies QuickSearchItem,
      ),
    },
    {
      heading: 'Orders',
      items: orders.map(
        (i) =>
          ({
            key: `orders-${i.id}`,
            to: `/${i.storeId}/orders/lookup/${i.idFromPlatform}`,
            externalId: i.orderNo,
            customerName: i.customerName ?? i.customerEmail,
            createdOn: i.createdOn,
            source: null,
            icon: 'order',
            storeName: storeNameOf(i.storeName),
          }) satisfies QuickSearchItem,
      ),
    },
    {
      heading: 'Shipping',
      items: shipping.map(
        (i) =>
          ({
            key: `shipping-${i.id}`,
            to: `/${i.storeId}/claims/shipping/${i.id}`,
            externalId: `#${i.id}`,
            customerName: i.customerName ?? i.customerEmail,
            createdOn: i.createdOn,
            source: null,
            icon: 'productGroup',
            storeName: storeNameOf(i.storeName),
          }) satisfies QuickSearchItem,
      ),
    },
    {
      heading: 'Registrations',
      items: registrations.map(
        (i) =>
          ({
            key: `registrations-${i.id}`,
            to: `/${i.storeId}/registrations/${i.id}`,
            externalId: `#${i.externalId}`,
            customerName: i.customerName ?? i.customerEmail,
            createdOn: i.createdOn,
            source: null,
            icon: 'registration',
            storeName: storeNameOf(i.storeName),
          }) satisfies QuickSearchItem,
      ),
    },
    {
      heading: 'Products',
      // products are only searched within the active store
      items: products.map(
        (i) =>
          ({
            key: `products-${i.id}`,
            to: `/${storeId}/products/${i.id}`,
            externalId: i.title,
            customerName: i.productType ?? null,
            createdOn: i.createdOn,
            source: i.isOffPlatform ? 'Corso' : 'Shopify',
            icon: 'product',
            storeName: null,
          }) satisfies QuickSearchItem,
      ),
    },
  ].filter((group) => group.items.length > 0);

  return { groups, isLoading, isRefetching };
}

export default function QuickSearch() {
  const trigger = useRef<HTMLButtonElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 500);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // one of the known use cases for the deprecated `platform` property
  const isApplePlatform = useMemo(
    () => /Mac|iPod|iPhone|iPad/.test(navigator.platform),
    [],
  );

  useEventListener('keydown', (event) => {
    if (event.key !== 'k') return;
    if (!(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    setPopoverOpen(true);
  });

  const {
    isReturnsEnabled,
    isWarrantyEnabled,
    isShippingProtectionEnabled,
    isRegistrationEnabled,
  } = useEnabledClaimType();

  const isConciergeSearch = useIsConciergeSearch();
  const searchBy = isConciergeSearch
    ? ['Shipping Claims']
    : [
        isReturnsEnabled && 'Returns',
        isWarrantyEnabled && 'Warranties',
        isShippingProtectionEnabled && 'Shipping Claims',
        isRegistrationEnabled && 'Registrations',
      ].filter(isTruthy);

  const searchByString = isConciergeSearch
    ? `Find ${searchBy.join(', ')} and Orders`
    : `Find ${searchBy.join(', ')}, Orders and Products`;
  const { groups, isLoading, isRefetching } = useSearchGroups(debouncedSearch);

  const handleItemSelect = () => setPopoverOpen(false);

  const isSearchTooLong =
    debouncedSearch.length > CrewMerchantUi.MAX_SEARCH_TERM_LENGTH;
  const isAwaitingResults = isLoading || isRefetching;

  const quickSearchIcon = useMemo(() => getQuickSearchIcon(), []);

  return (
    // omitting clearing debouncedSearch from onOpenChange allows the previous results to be displayed
    <Popover
      modal
      open={popoverOpen}
      onOpenChange={(open) => setPopoverOpen(open)}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="group w-full max-w-xl rounded-xl bg-linear-to-br from-neutral-600 via-neutral-700 to-neutral-600 p-px text-left shadow-md transition-all duration-100 hover:bg-linear-to-bl hover:from-neutral-700 hover:via-neutral-800 hover:to-neutral-700"
                ref={trigger}
              >
                <div className="flex w-full items-center rounded-xl bg-neutral-800 px-3 py-1.5 text-sm text-white/50 transition-all duration-100 group-hover:bg-neutral-950 group-has-[[data-state]:not([data-state='closed'])]:bg-red-700">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'flex items-center justify-center',
                          quickSearchIcon.emphasize === 'shimmer' &&
                            `relative -m-1 p-1 transition-all duration-300 group-hover:text-white group-has-[[data-state]:not([data-state='closed'])]:text-white before:absolute before:inset-0 before:ai-shimmer before:rounded-lg before:opacity-0 before:transition-all before:duration-300 group-hover:before:opacity-100 group-has-[[data-state]:not([data-state='closed'])]:before:opacity-100`,
                        )}
                      >
                        <FontAwesomeIcon
                          icon={quickSearchIcon.icon}
                          className="relative size-4"
                        />
                      </div>
                      Quick Search
                    </div>
                    <kbd className="flex items-center justify-center rounded-md border border-neutral-600 px-1 py-0.5 font-sans text-xs">
                      {isApplePlatform ? '⌘' : 'Ctrl'}&nbsp;K
                    </kbd>
                  </div>
                </div>
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          {quickSearchIcon.content && (
            <TooltipContent align="start">
              {quickSearchIcon.content}
            </TooltipContent>
          )}
        </Tooltip>
        <PopoverContent
          className="p-0"
          // overlap the content over the trigger
          sideOffset={-1 * (trigger.current?.offsetHeight ?? 0)}
        >
          {/* no need to filter, will instead show all results returned by API */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Quick Search"
              className="w-full"
              onValueChange={setDebouncedSearch}
            />

            {groups.length > 0 && (
              <div className="m-4 flex justify-start space-x-4">
                {groups.map((group) => (
                  <Badge key={group.heading}> {group.heading}</Badge>
                ))}
              </div>
            )}

            <CommandList>
              {isSearchTooLong && (
                <Alert variant="warning" heading="Search Term Too Long">
                  Please shorten your search term to continue.
                </Alert>
              )}

              {!isSearchTooLong && !isAwaitingResults && (
                <CommandEmpty>
                  <div>
                    <Icon
                      icon="search"
                      className="h-10 w-10 text-neutral-500"
                    />
                    <p className="m-2 mt-4 text-sm text-neutral-500">
                      {searchByString}
                    </p>
                  </div>
                </CommandEmpty>
              )}

              {!isSearchTooLong && isAwaitingResults && (
                <CommandLoading>
                  <Loading />
                </CommandLoading>
              )}

              {groups.map((group) => (
                <CommandGroup
                  key={group.heading}
                  heading={<p className="text-xs">{group.heading}</p>}
                >
                  {group.items.map((item) => (
                    <CommandItem key={item.key} onSelect={handleItemSelect}>
                      <QuickSearchListItem
                        to={item.to}
                        externalId={item.externalId}
                        customerName={item.customerName}
                        createdOn={item.createdOn}
                        source={item.source}
                        icon={item.icon}
                        storeName={item.storeName}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </TooltipProvider>
    </Popover>
  );
}
